import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";

import { WebchatWidget } from "./components/WebchatWidget";
import { initLogger } from "./logger";
import { changeExpandedStatus, updatePreEngagementData } from "./store/actions/genericActions";
import { PreEngagementData } from "./store/definitions";
import { store } from "./store/store";

import { webchatStyles } from "./webchatStyles";

const initWebchat = async ({ webchatConfigSid, accountSid, retry }: { webchatConfigSid: string, accountSid: string, retry?: number }) => {
    initLogger();
    const rootElement = document.getElementById("twilio-webchat-widget-root");

    if (rootElement) {
        let shadowRoot = rootElement.shadowRoot;

        if (!shadowRoot) {
            try {
                shadowRoot = rootElement.attachShadow({ mode: "open" });

                const shadowStyles = document.createElement('style');
                shadowStyles.textContent = webchatStyles;
                shadowRoot.appendChild(shadowStyles);

                const shadowContainer = document.createElement("div");
                shadowContainer.id = "webchat-shadow-container";
                shadowRoot.appendChild(shadowContainer);
            } catch (error) {
                console.error(error);
                const root = createRoot(rootElement);
                root.render(
                    <Provider store={store}>
                        <WebchatWidget webchatConfigSid={webchatConfigSid} accountSid={accountSid} />
                    </Provider>
                );
                return;
            }
        }

        const shadowContainer = shadowRoot?.getElementById("webchat-shadow-container");

        if (shadowContainer) {
            const root = createRoot(shadowContainer);
            root.render(
                <Provider store={store}>
                    <WebchatWidget webchatConfigSid={webchatConfigSid} accountSid={accountSid} />
                </Provider>
            );

            const styleObserver = new MutationObserver(() => {
                const twilioStyleSheets = document.querySelectorAll("style[data-s]")
                twilioStyleSheets.forEach(styleSheet => {
                    shadowRoot.appendChild(styleSheet);
                });
            });

            styleObserver.observe(document.head, {
                childList: true,
                subtree: true
            });

        }
    } else if (!retry || retry < 2) {
        setTimeout(() => {
            return initWebchat({ webchatConfigSid, accountSid, retry: retry ? retry + 1 : 1 });
        }, 200 * (retry ?? 1));
    }

    if (window.Cypress) {
        window.store = store;
    }
};

/**
 * Updates the pre-engagement data on the webchat
 * @param data A partial of the pre-engagement data e.g. { name: "John Doe" }
 */
const setFormData = (data: Partial<PreEngagementData>) => {
    store.dispatch(updatePreEngagementData(data));
};

/**
 * Opens the webchat
 */
const openWebchat = () => {
    store.dispatch(changeExpandedStatus({ expanded: true }));
};

/**
 * Closes the webchat
 */
const closeWebchat = () => {
    store.dispatch(changeExpandedStatus({ expanded: false }));
};

declare global {
    interface Window {
        Twilio: {
            initWebchat: (options: { webchatConfigSid: string, accountSid: string, retry?: number }) => void;
            setFormData: (data: Partial<PreEngagementData>) => void;
            openWebchat: () => void;
            closeWebchat: () => void;
        };
        Cypress: Cypress.Cypress;
        store: typeof store;
    }
}

// Expose `initWebchat` and `addAdditionalFormData` functions to window object
Object.assign(window, {
    Twilio: {
        initWebchat,
        setFormData,
        openWebchat,
        closeWebchat
    }
});

// Add this new code to initialize the webchat when the DOM is loaded
window.addEventListener("DOMContentLoaded", () => {

    const styleElement = document.createElement('style');
    styleElement.textContent = `
        #twilio-webchat-widget-root {
            position: fixed !important;
            z-index: 2147483645 !important;
            /* Let shadow DOM content determine size */
            width: auto !important;
            height: auto !important;
            display: block !important;
            overflow: visible !important;
        }
        #twilio-webchat-widget-root * {
            pointer-events: auto !important;
        }
        
        /* Modal overlay/backdrop */
        [data-paste-element="MODAL_OVERLAY"],
        [data-reach-dialog-overlay] {
            z-index: 2147483647 !important;
        }
        
        /* When modal is present, reduce webchat z-index */
        body:has([aria-modal="true"]) #twilio-webchat-widget-root {
            z-index: 2147483644 !important;
        }
    `;
    document.head.appendChild(styleElement);

    // Add this to create the root div if it doesn't exist
    if (!document.getElementById("twilio-webchat-widget-root")) {
        const rootDiv = document.createElement("div");
        rootDiv.id = "twilio-webchat-widget-root";
        document.body.appendChild(rootDiv);
    }
});